import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDebounce } from "react-use";
import { useGroopitApiContext } from "groopit-js";
import {
  GroopItModel_V1,
  GroopitFilterDisplayDTO,
  AssignmentDTO,
} from "groopit-js/client";
import useLatestRef from "../../react/react-use/useLatestRef";
import { GroopitFilter } from "../GroopitFilterDTO";
import { useFilterContext } from "./FilterContext";
import { GroopitFilterDisplay } from "../../Models/GroopitFilterDisplay";

interface FeedState {
  loading: boolean;
  token?: string;
  groopits: GroopItModel_V1[];
  assignments: { [key: string]: AssignmentDTO };
  hasHiddenFields: boolean;
  totalCount: number;
  isLimitedBySubscription: boolean;
}

interface FeedDispatch {
  loadMore: () => void;
  sortFeed: () => void;
}

const FeedContext = createContext<FeedState | undefined>(undefined);

const FeedDispatchContext = createContext<FeedDispatch | undefined>(undefined);

export const useFeedContext = () => {
  const context = useContext(FeedContext);
  if (!context) {
    throw "useFeedContext() invoked without a parent provider";
  }
  return context;
};

export const useFeedDispatchContext = () => {
  const context = useContext(FeedDispatchContext);
  if (!context) {
    throw "useFeedDispatchContext() invoked without a parent provider";
  }
  return context;
};

interface FeedProviderProps {
  groopId: string;
  filter?: GroopitFilterDisplayDTO;
  children: ReactNode;
}

export const FeedProvider = (props: FeedProviderProps): JSX.Element => {
  const page_size = 20;
  const { groopId, children } = props;
  const api = useLatestRef(useGroopitApiContext());

  const { filter } = useFilterContext();
  const [debouncedFilter, setDebouncedFilter] = useState<GroopitFilter>(filter);

  const [feed, setFeed] = useState<FeedState>({
    loading: true,
    groopits: [],
    assignments: {},
    hasHiddenFields: false,
    totalCount: 0,
    isLimitedBySubscription: false,
  });

  useDebounce(
    () => {
      setDebouncedFilter(filter);
    },
    500,
    [filter],
  );

  useEffect(() => {
    setFeed((prev) => {
      return { ...prev, loading: true };
    });
  }, [filter, setFeed]);

  useEffect(() => {
    api.current.groop
      .groopits(
        groopId,
        null,
        0,
        page_size,
        GroopitFilterDisplay.convertToGroopitFilter(debouncedFilter.filter),
      )
      .then((result) => {
        setFeed({
          loading: false,
          token: result.token,
          groopits: result.groopits,
          assignments: result.assignments,
          hasHiddenFields: result.hasHiddenFields,
          totalCount: result.totalCount,
          isLimitedBySubscription: result.isLimitedBySubscription,
        });
      })
      .catch(() => {
        setFeed((prev) => {
          return { ...prev, loading: false };
        });
      });
  }, [groopId, debouncedFilter, api]);

  const sortFeed = useCallback(() => {
    setFeed((prev) => {
      return {
        ...prev,
        groopits: prev.groopits.sort((a, b) => {
          if (a.pinned != b.pinned) {
            return a.pinned ? -1 : 1;
          } else {
            return a.timestamp > b.timestamp ? -1 : 1;
          }
        }),
      };
    });
  }, [setFeed]);

  const loadMore = useCallback(() => {
    if (!feed.loading && feed.totalCount > feed.groopits.length) {
      setFeed({ ...feed, loading: true });
      api.current.groop
        .groopits(
          groopId,
          feed.token,
          feed.groopits.length,
          page_size,
          GroopitFilterDisplay.convertToGroopitFilter(filter.filter),
        )
        .then((result) => {
          const posts = feed.groopits.concat(result.groopits);
          setFeed({ ...feed, groopits: posts, loading: false });
        });
    }
  }, [feed, setFeed, groopId, filter, api]);

  return (
    <FeedContext.Provider value={feed}>
      <FeedDispatchContext.Provider
        value={{
          loadMore,
          sortFeed,
        }}
      >
        {children}
      </FeedDispatchContext.Provider>
    </FeedContext.Provider>
  );
};
